import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import { IMLParcelColumnsProps } from '@/types/plugins/mlParcel';
import { formatDate } from '@/utils/common';

import { getStatusOrderBg, getStatusOrderColor, getStatusOrderLabel } from './constants';

export const mlparcelColumns = ({
  handleCreateOrder,
  creatingOrderIds,
  handleDownloadLabel,
}: IMLParcelColumnsProps): Array<GridColDef> => [
  {
    field: 'orderId',
    flex: 1,
    headerName: 'N° de Venta Mercado Libre',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      const packId = params.row.packId;
      const hasPackId = typeof packId === 'string' && packId.trim() !== '';

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
          gap={0.5}
        >
          <Typography variant="body2" display="inline" sx={{ fontWeight: 'bold' }}>
            {params.row.orderId}
          </Typography>
          {hasPackId && (
            <Chip
              icon={<Inventory2OutlinedIcon fontSize="small" />}
              label={`Paquete: ${packId}`}
              size="small"
            />
          )}
        </Box>
      );
    },
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Fecha de pedido',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Typography
        variant="body2"
        display="inline"
        sx={{ color: (theme) => theme.palette.grey[600] }}
      >
        {formatDate(params.row.createdAt, 'dd/MM/yyyy')}
      </Typography>
    ),
  },
  {
    field: 'statusOrder',
    flex: 1,
    headerName: 'Estatus Mercado Libre',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Box>
        <StatusChip
          status={getStatusOrderLabel(params.row.statusOrder)}
          color={getStatusOrderColor(params.row.statusOrder)}
          bg={getStatusOrderBg(params.row.statusOrder)}
        />
      </Box>
    ),
  },
  {
    field: 'trackingId',
    flex: 1,
    headerName: 'Nº de seguimiento',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => (
      <Typography
        variant="body2"
        display="inline"
        sx={{
          fontWeight: params.row.trackingId?.length > 0 ? 'bold' : 'normal',
          color: (theme) =>
            params.row.trackingId?.length > 0
              ? theme.palette.primary.main
              : theme.palette.grey[500],
        }}
      >
        {params.row.trackingId?.length > 0 ? params.row.trackingId : '-'}
      </Typography>
    ),
  },
  {
    field: 'logisticMode',
    flex: 1,
    headerName: 'Modo / Tipo logístico',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      const mode = params.row.orderData?.logisticMode || '-';
      const type = params.row.orderData?.logisticType || '-';
      return (
        <Typography
          variant="body2"
          display="inline"
          sx={{ color: (theme) => theme.palette.grey[600] }}
        >
          {mode} / {type}
        </Typography>
      );
    },
  },
  {
    field: 'actions',
    flex: 0.5,
    headerName: 'Acciones',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    resizable: false,
    renderCell: (params) => {
      const { id, trackingId, status, statusOrder } = params.row;
      const canDownload = trackingId?.length > 0;
      const canCreate =
        status !== 'created' && statusOrder !== 'cancelled' && statusOrder !== 'pending_cancel';
      const isCreating = creatingOrderIds.has(id);

      return (
        <Box
          sx={{ display: 'flex', gap: '0.25rem', alignItems: 'center', justifyContent: 'center' }}
        >
          <Tooltip
            followCursor
            title={
              status === 'created'
                ? 'Orden ya generada'
                : canCreate
                  ? 'Crear orden en 99minutos Parcel'
                  : 'No disponible para este estado'
            }
            placement="top"
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={!canCreate || isCreating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateOrder(id);
                }}
              >
                {isCreating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            followCursor
            title={canDownload ? 'Descarga de etiqueta' : 'Sin etiqueta disponible'}
            placement="top"
          >
            <span>
              <IconButton
                color="primary"
                disabled={!canDownload}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canDownload) {
                    handleDownloadLabel(trackingId);
                  }
                }}
              >
                <CloudDownloadOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
];
